import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-hot-toast';
import AuthLayout from './auth/AuthLayout';
import { supabase } from '@/lib/supabase';
import Button from '@/components/form/Button';
import Input from '@/components/form/Input';
import { MailIcon, KeyIcon, AlertCircleIcon } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if there's a redirect SNAP ID in localStorage or URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectId = params.get('redirect');
    
    if (redirectId) {
      localStorage.setItem('redirectSnapId', redirectId);
    }
    
    const redirectSnapId = localStorage.getItem('redirectSnapId');
    if (redirectSnapId) {
      console.log('Found redirect SNAP ID:', redirectSnapId);
    }
  }, [location]);

  // Clear any invalid tokens on component mount
  useEffect(() => {
    const clearInvalidTokens = async () => {
      try {
        // Check if there's an invalid token error
        const { data, error } = await supabase.auth.getSession();
        if (error && error.message.includes('refresh_token_not_found')) {
          // Clear any invalid tokens
          await supabase.auth.signOut();
          localStorage.clear();
          document.cookie.split(';').forEach(cookie => {
            document.cookie = cookie
              .replace(/^ +/, '')
              .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
          });
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    };

    clearInvalidTokens();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setNeedsConfirmation(false);

      const { error } = await signIn(email, password);
      
      if (error) {
        if (error.message.includes('Email not confirmed')) {
          setNeedsConfirmation(true);
          return;
        }
        throw error;
      }

      // Check if there's a redirect SNAP ID in localStorage
      const redirectSnapId = localStorage.getItem('redirectSnapId');
      if (redirectSnapId) {
        // Remove the item from localStorage
        localStorage.removeItem('redirectSnapId');
        // Navigate to the SNAP
        navigate(`/snap/${redirectSnapId}`);
      } else {
        // Navigate to dashboard if no redirect
        navigate('/dashboard');
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('Invalid login credentials')) {
          toast.error('Invalid email or password');
        } else {
          toast.error('Failed to sign in. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success('Password reset instructions sent to your email');
      setResetMode(false);
    } catch (error) {
      toast.error('Failed to send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) throw error;

      toast.success('Confirmation email resent successfully');
    } catch (error) {
      toast.error('Failed to resend confirmation email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form className="space-y-6" onSubmit={resetMode ? handleResetPassword : handleSubmit}>
        {needsConfirmation && (
          <div className="rounded-md bg-yellow-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Email confirmation required
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Please check your email and click the confirmation link to activate your account.
                    {' '}
                    <button
                      type="button"
                      onClick={handleResendConfirmation}
                      className="font-medium text-yellow-800 underline hover:text-yellow-900"
                    >
                      Resend confirmation email
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email address
          </label>
          <div className="mt-1">
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              icon={<MailIcon className="h-5 w-5" />}
            />
          </div>
        </div>

        {!resetMode && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-1">
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={<KeyIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        )}

        <div>
          <Button
            type="submit"
            className="w-full"
            loading={loading}
          >
            {resetMode ? 'Send Reset Instructions' : 'Sign in'}
          </Button>
        </div>

        <div className="text-sm text-center space-y-2">
          <button
            type="button"
            onClick={() => setResetMode(!resetMode)}
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            {resetMode ? 'Back to Sign in' : 'Forgot your password?'}
          </button>
          <div>
            <Link
              to="/register"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Don't have an account? Sign up
            </Link>
          </div>
        </div>
      </form>
    </AuthLayout>
  );
}